export type UserRole =
  | 'ADMIN'
  | 'MANAGEMENT'
  | 'PROJECT_MANAGER'
  | 'SITE_ENGINEER'
  | 'PURCHASE_OFFICER'
  | 'ACCOUNTS'
  | 'DATA_ENTRY'
  | 'VIEWER'
  | 'SUPERVISOR'
  | 'FIELD_STAFF'
  | 'TECHNICIAN';

export interface ModuleItem {
  id: number;
  code: string;
  name: string;
  category: 'operations' | 'procurement' | 'finance' | 'people' | 'system';
  icon: string;
  description: string;
  isCore: boolean;
  isEnabled: boolean;
  dependencies?: string[] | null;
}

export interface UserItem {
  id: number;
  uid: string;
  name: string;
  email: string;
  role: UserRole;
  phone?: string;
  designation?: string;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
}

export interface RoleItem {
  id: number;
  code: UserRole;
  name: string;
  description: string;
  permissions: string[];
}

export interface CompanyItem {
  id: number;
  code: string;
  name: string;
  ntn?: string;
  strn?: string;
  address?: string;
  phone?: string;
  email?: string;
  currency: string;
  isDefault: boolean;
}

export interface CustomerItem {
  id: number;
  code: string;
  name: string;
  category: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  address?: string;
  status: string;
}

export interface BranchItem {
  id: number;
  customerId: number;
  branchCode: string;
  branchName: string;
  city: string;
  region: string;
  address: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  ublEngineer?: string;
  assignedTeam?: string;
  latitude?: string | null;
  longitude?: string | null;
  allowedRadiusMeters?: number | null;
  status: string;
}

export type TicketStatus =
  | 'NEW'
  | 'ASSIGNED'
  | 'SITE_SURVEY'
  | 'BOQ'
  | 'QUOTATION'
  | 'APPROVAL'
  | 'PO_RECEIVED'
  | 'WORK_IN_PROGRESS'
  | 'COMPLETED'
  | 'VERIFICATION_PENDING'
  | 'VERIFIED'
  | 'CERTIFICATE'
  | 'INVOICE'
  | 'PAYMENT'
  | 'CLOSED';

export interface TicketItem {
  id: number;
  ticketNumber: string;
  branchId: number;
  branchCode?: string;
  branchName?: string;
  city?: string;
  region?: string;
  category: string;
  subject: string;
  description: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  assignedTo?: string;
  targetDate?: string;
  status: TicketStatus;
  remarks?: string;
  complaintNumber?: string;
  vendorId?: number | null;
  vendorName?: string;
  vendorContactPerson?: string;
  verificationStatus?: string | null;
  verifiedBy?: string | null;
  verifiedAt?: string | null;
  workflowType?: WorkflowType;
  createdBy: string;
  createdAt: string;
}

export type ProjectStatus =
  | 'PLANNING'
  | 'SURVEY'
  | 'BOQ_PENDING'
  | 'QUOTATION_SUBMITTED'
  | 'PO_AWAITED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'INVOICED'
  | 'CLOSED';

export interface ProjectItem {
  id: number;
  projectNumber: string;
  projectName: string;
  customerId: number;
  branchId: number;
  branchName?: string;
  branchCode?: string;
  city?: string;
  region?: string;
  ticketId?: number | null;
  poNumber?: string | null;
  poDate?: string | null;
  poAmount?: string;
  projectManager: string;
  siteEngineer: string;
  startDate: string;
  targetCompletionDate: string;
  actualCompletionDate?: string | null;
  status: ProjectStatus;
  contractValue: string;
  budget: string;
  actualCost: string;
  profit: string;
  createdAt: string;
}

export interface SiteSurveyItem {
  id: number;
  surveyNumber: string;
  projectId: number;
  projectNumber?: string;
  projectName?: string;
  ticketId?: number | null;
  workOrderId?: number | null;
  branchId: number;
  branchName?: string;
  branchCode?: string;
  surveyDate: string;
  engineer: string;
  existingCondition: string;
  measurements: string;
  requiredWork: string;
  requiredMaterials: string;
  remarks?: string;
  signatureName?: string;
  latitude?: string | null;
  longitude?: string | null;
  isGpsVerified?: boolean;
  status: 'DRAFT' | 'SUBMITTED' | 'APPROVED';
  approvedBy?: string | null;
  approvedAt?: string | null;
  createdAt: string;
}

export interface BOQItemLine {
  id?: number;
  boqId?: number;
  itemNumber: number;
  description: string;
  unit: string;
  quantity: string | number;
  materialRate: string | number;
  labourRate: string | number;
  otherCost: string | number;
  marginPercent: string | number;
  unitRate?: string | number;
  totalAmount?: string | number;
  serviceId?: number | null;
  assemblyId?: number | null;
  ticketId?: number | null;
  estimationId?: number | null;
}

export interface BOQItem {
  id: number;
  boqNumber: string;
  projectId: number;
  projectNumber?: string;
  projectName?: string;
  revisionNumber: number;
  title: string;
  subtotal: string;
  taxPercent: string;
  taxAmount: string;
  grandTotal: string;
  status: 'DRAFT' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED';
  approvedBy?: string;
  approvedAt?: string;
  remarks?: string;
  items?: BOQItemLine[];
  createdAt: string;
}

export interface QuotationItem {
  id: number;
  quotationNumber: string;
  projectId: number;
  projectNumber?: string;
  projectName?: string;
  boqId: number;
  revisionNumber: number;
  date: string;
  validityDays: number;
  subtotal: string;
  discount: string;
  taxPercent: string;
  taxAmount: string;
  grandTotal: string;
  terms: string;
  status: 'PENDING_APPROVAL' | 'APPROVED' | 'SENT_TO_UBL' | 'ACCEPTED' | 'REJECTED';
  approvedBy?: string;
  createdAt: string;
}

export interface VendorItem {
  id: number;
  code: string;
  name: string;
  category: string;
  supplierCategory?: 'REGISTERED' | 'UNREGISTERED' | 'IMPORTER' | 'MANUFACTURER' | 'CONTRACTOR' | 'SERVICE_PROVIDER';
  contactPerson?: string;
  phone: string;
  email?: string;
  address?: string;
  ntn?: string;
  strn?: string | null;
  hasGstInvoiceCapability?: boolean;
  incomeTaxClassification?: string;
  isTaxExempt?: boolean;
  openingBalance?: string | number;
  paymentTerms: string;
  status: string;
}

export interface PurchaseOrderItem {
  id: number;
  poNumber: string;
  prId?: number | null;
  projectId: number;
  projectNumber?: string;
  projectName?: string;
  vendorId: number;
  vendorName?: string;
  poDate: string;
  deliveryDate?: string;
  subtotal: string;
  taxAmount: string;
  grandTotal: string;
  status: 'ISSUED' | 'RECEIVED_PARTIAL' | 'RECEIVED_FULL' | 'BILLED' | 'CLOSED';
  terms?: string;
  createdAt: string;
}

export interface LegacyInventoryItem {
  id: number;
  code: string;
  name: string;
  category: string;
  unit: string;
  standardCost: string;
  currentStock: string;
  reorderLevel: string;
}
export type LegacyItem = LegacyInventoryItem;

export interface StockTransactionItem {
  id: number;
  transactionType: 'PURCHASE_IN' | 'PROJECT_ISSUE' | 'RETURN_TO_STORE';
  itemId: number;
  itemName?: string;
  unit?: string;
  projectId?: number | null;
  projectNumber?: string;
  quantity: string;
  unitCost: string;
  totalCost: string;
  referenceNo?: string;
  remarks?: string;
  createdAt: string;
}

export interface ProjectTaskItem {
  id: number;
  projectId: number;
  taskName: string;
  category: string;
  weightage: number;
  progressPercent: number;
  startDate?: string;
  targetDate?: string;
  completionDate?: string;
  assignedTo?: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  remarks?: string;
}

export interface CompletionCertificateItem {
  id: number;
  certificateNumber: string;
  projectId: number;
  projectNumber?: string;
  projectName?: string;
  ticketId?: number | null;
  branchId: number;
  branchName?: string;
  branchCode?: string;
  poNumber?: string;
  workDescription: string;
  completionDate: string;
  engineer: string;
  ublRepresentative: string;
  status: 'PENDING' | 'SUBMITTED' | 'UNDER_VERIFICATION' | 'APPROVED' | 'RECEIVED';
  certificateFile?: string;
  remarks?: string;
  createdAt: string;
}

export interface InvoiceItem {
  id: number;
  invoiceNumber: string;
  projectId: number;
  projectNumber?: string;
  projectName?: string;
  customerId: number;
  branchId: number;
  branchName?: string;
  branchCode?: string;
  poNumber?: string;
  completionCertId?: number | null;
  pccId?: number | null;
  pccNumber?: string | null;
  pccVerified?: boolean;
  invoiceDate: string;
  dueDate: string;
  subtotal: string;
  taxPercent: string;
  taxAmount: string;
  grandTotal: string;
  paidAmount: string;
  status: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'SENT' | 'PARTIALLY_PAID' | 'PAID' | 'OVERDUE';
  remarks?: string;
  createdAt: string;
}

export interface ConsolidatedInvoiceChildItem extends InvoiceItem {
  allocatedAmount?: string;
}

export interface ConsolidatedInvoiceItem {
  id: number;
  consolidatedNumber: string;
  customerId: number;
  title: string;
  issueDate: string;
  dueDate: string;
  totalAmount: string;
  paidAmount: string;
  status: 'DRAFT' | 'SUBMITTED' | 'PARTIALLY_PAID' | 'PAID';
  remarks?: string;
  invoices?: ConsolidatedInvoiceChildItem[];
  createdAt: string;
}

export interface AccountItem {
  id: number;
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  balance: string;
  description?: string;
}

export interface PaymentItem {
  id: number;
  paymentNumber: string;
  paymentDate: string;
  paymentType: 'RECEIVED' | 'PAID';
  referenceType: string;
  referenceId: number;
  amount: string;
  paymentMethod: string;
  accountId?: number;
  accountName?: string;
  accountCode?: string;
  chequeOrRefNo?: string;
  remarks?: string;
  createdAt: string;
}

export interface ExpenseItem {
  id: number;
  expenseNumber: string;
  expenseDate: string;
  projectId?: number | null;
  projectNumber?: string;
  projectName?: string;
  category: 'Transport' | 'Fuel' | 'Labour' | 'Site Expense' | 'Food' | 'Material' | 'Miscellaneous';
  amount: string;
  paidTo?: string;
  paymentMethod: string;
  description: string;
  receiptUrl?: string;
  createdAt: string;
}

export interface ProjectCostingSummary {
  id: number;
  projectNumber: string;
  projectName: string;
  contractValue: number;
  budget: number;
  materialCost: number;
  labourCost: number;
  transportCost: number;
  otherCost: number;
  totalActual: number;
  grossProfit: number;
  marginPercent: number;
}

export interface AuditLogItem {
  id: number;
  userName: string;
  action: string;
  module: string;
  recordId: string;
  recordType: string;
  oldStatus?: string;
  newStatus?: string;
  details?: string;
  createdAt: string;
}

export interface DocumentItem {
  id: number;
  title: string;
  category: string;
  projectId?: number | null;
  projectNumber?: string;
  fileUrl: string;
  fileType: string;
  fileSize: string;
  uploadedBy: string;
  createdAt: string;
}

export interface NotificationItem {
  id: number;
  title: string;
  message: string;
  type: 'INFO' | 'SUCCESS' | 'WARNING' | 'ALERT';
  isRead: boolean;
  link?: string;
  createdAt: string;
}

// HERE4U 9 Work Types
export const HERE4U_WORK_TYPES = [
  'AC',
  'Blinds',
  'Electrical',
  'Genset',
  'Glass / Door / Wood Work',
  'Grill / Window / Other',
  'Paint / Tile / Seepage / Front Elevation',
  'Plumbing',
  'Ramps',
] as const;

export type Here4uWorkType =
  | (typeof HERE4U_WORK_TYPES)[number]
  | 'Grill / Window / Genset / Other'
  | 'Grill & Window Work'
  | 'Ramps & Miscellaneous Civil Works';

export type WorkflowType = 'NORMAL' | 'MINOR_URGENT';

export interface TemporaryCaseItem {
  id: number;
  tempCaseNumber: string;
  branchCode: string;
  branchName: string;
  branchId?: number | null;
  issueSubject: string;
  workType?: string | null;
  gmailThreadId: string;
  gmailMessageId?: string | null;
  senderEmail: string;
  senderName?: string | null;
  receivedAt: string;
  status: 'PENDING_TICKET_NUMBER' | 'MATCHED_TO_TICKET' | 'CANCELLED';
  matchedTicketId?: number | null;
  matchedTicketNumber?: string | null;
  matchedAt?: string | null;
  notes?: string | null;
  createdAt: string;
}

export interface GmailDraftItem {
  id: number;
  draftNumber: string;
  ticketId?: number | null;
  tempCaseId?: number | null;
  toEmail: string;
  ccEmail?: string | null;
  subject: string;
  body: string;
  attachments?: string[] | null;
  status: 'DRAFT' | 'READY_FOR_REVIEW' | 'APPROVED' | 'REJECTED' | 'SENDING' | 'SENT' | 'FAILED' | 'CANCELLED';
  createdBy: string;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  sentBy?: string | null;
  sentAt?: string | null;
  gmailMessageId?: string | null;
  gmailThreadId?: string | null;
  errorReason?: string | null;
  isSimulated?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface GmailMessageItem {
  id: number;
  threadId: string;
  messageId: string;
  fromEmail: string;
  fromName?: string | null;
  toEmail: string;
  subject: string;
  snippet: string;
  body: string;
  extractedTicketNumber?: string | null;
  extractedBranchCode?: string | null;
  extractedWorkType?: string | null;
  hasAttachments: boolean;
  attachments?: string[] | null;
  receivedAt: string;
  matchedTicketId?: number | null;
  ticketId?: number | null;
  matchedTempCaseId?: number | null;
  tempCaseId?: number | null;
  temporaryCaseId?: number | null;
  status: 'PARSED' | 'TICKET_MATCHED' | 'TEMP_CASE_CREATED' | 'REQUIRES_MANUAL_REVIEW' | 'IGNORED';
  reviewReason?: string | null;
  isSent: boolean;
  isSimulated?: boolean;
  createdAt: string;
}

export type StaffType = 'FIELD_ENGINEER' | 'SUPERVISOR' | 'TECHNICIAN' | 'CONTRACTOR' | 'SUBCONTRACTOR' | 'ESTIMATOR';
export type StaffAvailability = 'AVAILABLE' | 'ASSIGNED' | 'ON_SITE' | 'BUSY' | 'OFF_DUTY' | 'LEAVE' | 'INACTIVE';

export type SkillProficiency = 'Trainee' | 'Basic' | 'Intermediate' | 'Advanced' | 'Specialist';
export type SkillVerificationStatus = 'UNVERIFIED' | 'VERIFIED' | 'REJECTED';

export interface SkillItem {
  id: number;
  skillCode: string;
  name: string;
  category: string;
  description?: string | null;
  isActive: boolean;
  createdAt?: string;
}

export interface EmployeeSkillItem {
  id: number;
  staffId: number;
  skillId: number;
  isPrimary: boolean;
  proficiency: SkillProficiency;
  verificationStatus: SkillVerificationStatus;
  verifiedBy?: string | null;
  verifiedAt?: string | null;
  qualificationRef?: string | null;
  notes?: string | null;
  createdAt?: string;
  skillCode?: string;
  skillName?: string;
  category?: string;
}

export interface StaffItem {
  id: number;
  employeeId: string;
  name: string;
  mobile: string;
  cnic?: string | null;
  email?: string | null;
  role: string;
  staffType: StaffType;
  designation: string;
  skill: string;
  workTypes?: string[] | null;
  assignedSkills?: EmployeeSkillItem[];
  supervisorId?: number | null;
  supervisorName?: string | null;
  teamName: string;
  availability: StaffAvailability;
  joiningDate?: string | null;
  profilePhoto?: string | null;
  notes?: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface TeamItem {
  id: number;
  teamCode: string;
  teamName: string;
  supervisorId?: number | null;
  supervisorName?: string | null;
  region: string;
  primaryWorkType?: string | null;
  memberStaffIds?: number[] | null;
  members?: StaffItem[];
  status: 'ACTIVE' | 'INACTIVE';
  createdAt?: string;
}

export type WorkOrderStatus =
  | 'ASSIGNED'
  | 'TRAVELING'
  | 'EN_ROUTE'
  | 'ON_SITE'
  | 'SURVEYING'
  | 'MATERIAL_PENDING'
  | 'READY_TO_START'
  | 'IN_PROGRESS'
  | 'PAUSED'
  | 'COMPLETED'
  | 'VERIFICATION_PENDING'
  | 'VERIFIED'
  | 'CLOSED'
  | 'CANCELLED';

export interface WorkOrderItem {
  id: number;
  workOrderNumber: string;
  ticketId: number;
  ticketNumber?: string;
  temporaryCaseId?: number | null;
  branchId: number;
  branchCode?: string;
  branchName?: string;
  branchAddress?: string;
  branchContact?: string;
  workType: string;
  scope: string;
  description?: string;
  priority?: string;
  supervisorName: string;
  teamName: string;
  responsibleStaffId?: number | null;
  responsibleStaffName?: string | null;
  assignedStaff: string[];
  requiredMaterials?: string | null;
  requiredTools?: string | null;
  plannedDate?: string | null;
  dueDate?: string | null;
  expectedStartDate: string;
  expectedCompletionDate: string;
  instructions?: string | null;
  gpsRequired: boolean;
  workflowType: WorkflowType;
  status: WorkOrderStatus;
  progressStage?: string | null;
  progressPercent?: number | null;
  completedAt?: string | null;
  createdAt: string;
}

export interface FieldAttendanceItem {
  id: number;
  ticketId?: number | null;
  workOrderId?: number | null;
  staffId?: number | null;
  staffName: string;
  eventType: 'SITE_SURVEY' | 'CHECK_IN' | 'WORK_START' | 'PROGRESS' | 'COMPLETION' | 'CHECK_OUT';
  latitude: string;
  longitude: string;
  distanceMeters?: number | null;
  isGpsVerified: boolean;
  branchCode?: string | null;
  notes?: string | null;
  timestamp: string;
}

export interface WorkEvidenceItem {
  id: number;
  ticketId: number;
  workOrderId?: number | null;
  phase: 'BEFORE' | 'DURING' | 'AFTER' | 'SURVEY' | 'COMPLETION';
  caption: string;
  photoUrl: string;
  latitude?: string | null;
  longitude?: string | null;
  isGpsVerified: boolean;
  uploadedBy: string;
  uploadedAt: string;
}

export interface EstimatorTemplateItem {
  id: number;
  workTypeCode: string;
  workTypeName: string;
  description: string;
  defaultUnit: string;
  defaultWastagePercent: number;
  defaultOverheadPercent: number;
  defaultMarkupPercent: number;
  defaultTaxPercent: number;
  itemsJson: string; // parsed as EstimatorLineTemplate[]
}

export interface EstimatorLineTemplate {
  itemNumber: number;
  description: string;
  unit: string;
  defaultQty: number;
  materialRate: number;
  labourRate: number;
  equipmentRate?: number;
  transportRate?: number;
  subcontractorRate?: number;
  otherCost?: number;
}

// ============================================================================
// ==================== PHASE A: MASTER DATA & ESTIMATION TYPES ===============
// ============================================================================

export interface MaterialItem {
  id: number;
  itemCode: string;
  itemName: string;
  shortName?: string | null;
  category: string;
  subcategory?: string | null;
  unit: string;
  brandMake?: string | null;
  specification?: string | null;
  size?: string | null;
  thickness?: string | null;
  materialGrade?: string | null;
  colourFinish?: string | null;
  isStockItem: boolean;
  isPurchasable: boolean;
  minStock: string | number;
  reorderLevel: string | number;
  technicalNotes?: string | null;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt?: string;
}

export interface DimensionFieldSchema {
  name: string;
  label: string;
  unit: string;
  defaultValue?: number;
  min?: number;
  max?: number;
}

export interface OptionFieldSchema {
  name: string;
  label: string;
  options: string[];
  defaultValue?: string;
}

export interface WorkAssemblyItem {
  id: number;
  assemblyCode: string;
  assemblyName: string;
  here4uWorkType: string;
  category: string;
  inputDimensionsSchema: string; // JSON: DimensionFieldSchema[]
  inputOptionsSchema?: string | null; // JSON: OptionFieldSchema[]
  defaultWastagePercent: string | number;
  labourFormulaDescription?: string | null;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt?: string;
}

export interface AssemblyFormulaVersionItem {
  id: number;
  assemblyId: number;
  versionNumber: string; // e.g. 'V1.0', 'V1.1'
  effectiveDate: string;
  status: 'DRAFT' | 'ACTIVE' | 'SUPERSEDED' | 'DEPRECATED';
  formulaNotes?: string | null;
  createdBy: string;
  createdAt?: string;
}

export interface AssemblyComponentItem {
  id: number;
  formulaVersionId: number;
  materialId: number;
  material?: MaterialItem;
  componentRole: string; // Carcass, Shutter, Adhesive, Edging, Hardware, Grout
  quantityFormula: string; // e.g. '(length * height * 2 + depth * height * 2) / 32'
  unit: string;
  wastagePercent: string | number;
  labourHoursFormula?: string | null; // Zero prices
  notes?: string | null;
}

export interface ServiceItem {
  id: number;
  serviceCode: string;
  serviceName: string;
  category: string;
  subcategory?: string | null;
  boqDescription: string; // Official client-facing description for UBL
  unit: string;
  relatedAssemblyId?: number | null;
  relatedFormulaVersionId?: number | null;
  technicalStructure?: string | null;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt?: string;
}

export interface CalculatedBomLine {
  materialId: number;
  materialCode: string;
  materialName: string;
  componentRole: string;
  unit: string;
  baseQuantity: number;
  wastagePercent: number;
  wastageQuantity: number;
  requiredQuantity: number;
}

export interface EstimationItem {
  id: number;
  estimationNumber: string;
  ticketId: number;
  branchId: number;
  branchCode?: string;
  branchName?: string;
  estimatorId: number; // Foreign key to users.id
  estimatorName: string; // Display snapshot
  workType: string;
  assemblyId: number;
  assemblyName?: string;
  assemblyFormulaVersionId: number;
  formulaVersionNumber?: string;
  inputDimensions: string; // JSON of values: { length: 10, height: 7, depth: 2 }
  selectedOptions?: string | null;
  formulaSnapshot: string; // JSON: Frozen copy of formulas used at calculation time
  calculatedBom: string; // JSON: CalculatedBomLine[]
  calculatedQuantities: string; // JSON summary
  wastageSummary: string; // JSON summary
  requiredQuantities: string; // JSON summary
  materialRequirementId?: number | null;
  boqId?: number | null;
  boqItemId?: number | null;
  photos?: string[] | null;
  latitude?: string | null;
  longitude?: string | null;
  revision: number;
  status: 'CALCULATED' | 'SUBMITTED' | 'APPROVED_FOR_BOQ' | 'SUPERSEDED';
  createdAt: string;
}

export interface MaterialRequirementItem {
  id: number;
  ticketId: number;
  workOrderId?: number | null;
  estimationId: number;
  materialId: number;
  materialCode?: string;
  materialName?: string;
  unit?: string;
  calculatedQuantity: string | number;
  wastageQuantity: string | number;
  requiredQuantity: string | number;
  requestedQuantity: string | number;
  varianceQuantity: string | number;
  approvedQuantity?: string | number | null;
  fulfilledQuantity: string | number;
  usedQuantity?: string | number | null;
  remainingQuantity?: string | number | null;
  fulfillmentStatus: 'PENDING' | 'APPROVED' | 'PO_ISSUED' | 'CASH_PURCHASED' | 'RECEIVED_ON_SITE';
  approvedBy?: string | null;
  approvedAt?: string | null;
  notes?: string | null;
  createdAt: string;
}

export interface ContractorMaterialBillItem {
  id: number;
  billNumber: string;
  ticketId: number;
  workOrderId?: number | null;
  materialRequirementId?: number | null;
  contractorName: string;
  materialId: number;
  materialName?: string;
  quantity: string | number;
  amountPaid: string | number; // Cash cost in PKR
  supplierName?: string | null;
  invoiceOrBillNumber?: string | null;
  receiptPhotoUrl: string;
  paymentSource: 'ACCOUNTS_ADVANCE' | 'CONTRACTOR_OWN_FUNDS';
  advanceAccountId?: number | null;
  verificationStatus: 'PENDING' | 'VERIFIED_BY_SUPERVISOR' | 'REJECTED';
  verifiedBy?: string | null;
  verifiedAt?: string | null;
  settlementStatus: 'PENDING' | 'SETTLED' | 'REIMBURSED' | 'ADJUSTED_AGAINST_ADVANCE';
  settledAt?: string | null;
  remarks?: string | null;
  createdAt: string;
}

export interface PurchaseRequestLineItem {
  id: number;
  prId: number;
  materialId: number;
  materialName?: string;
  quantity: string | number;
  requiredDate?: string | null;
  status: 'PENDING' | 'ORDERED' | 'CANCELLED';
}

export interface PurchaseOrderLineItem {
  id: number;
  poId: number;
  materialId: number;
  materialName?: string;
  quantity: string | number;
  unitPrice: string | number;
  taxPercent: string | number;
  totalAmount: string | number;
}

export interface GoodsReceiptNoteItem {
  id: number;
  grnNumber: string;
  poId?: number | null;
  ticketId?: number | null;
  receivedDate: string;
  receivedBy: string;
  deliveryChallanNo?: string | null;
  inspectionStatus: 'ACCEPTED' | 'PARTIAL' | 'REJECTED';
  remarks?: string | null;
  createdAt: string;
}

export interface GRNLineItem {
  id: number;
  grnId: number;
  materialId: number;
  materialName?: string;
  receivedQuantity: string | number;
  rejectedQuantity: string | number;
  remarks?: string | null;
}

export interface RFQItem {
  id: number;
  rfqNumber: string;
  prId?: number | null;
  ticketId?: number | null;
  title: string;
  issueDate: string;
  submissionDeadline: string;
  status: 'OPEN' | 'EVALUATING' | 'CLOSED' | 'CANCELLED';
  remarks?: string | null;
  createdAt: string;
}

export interface VendorQuotationItem {
  id: number;
  rfqId: number;
  vendorId: number;
  vendorName?: string;
  quotationRefNo?: string | null;
  quoteDate: string;
  validUntil?: string | null;
  subtotal: string | number;
  taxAmount: string | number;
  grandTotal: string | number;
  deliveryTimelineDays?: number | null;
  paymentTerms?: string | null;
  status: 'SUBMITTED' | 'UNDER_REVIEW' | 'SELECTED' | 'REJECTED';
  remarks?: string | null;
  createdAt: string;
}

// ================= PHASE C: SITE EXECUTION & VARIATIONS =================

export type WorkProgressStage =
  | 'NOT_STARTED'
  | 'MATERIAL_PENDING'
  | 'MATERIAL_RECEIVED'
  | 'WORK_STARTED'
  | '25%'
  | '50%'
  | '75%'
  | 'COMPLETED'
  | 'VERIFIED';

export interface WorkProgressLogItem {
  id: number;
  workOrderId: number;
  ticketId?: number | null;
  staffId?: number | null;
  staffName: string;
  progressStage: string;
  progressPercent: number;
  description: string;
  materialUsage?: string | null;
  remainingMaterial?: string | null;
  photos?: string[] | null;
  latitude?: string | null;
  longitude?: string | null;
  isGpsVerified?: boolean;
  timestamp: string;
}

export type VariationStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'APPROVAL_PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED';

export interface WorkVariationItem {
  id: number;
  variationNumber: string;
  ticketId: number;
  workOrderId: number;
  materialId?: number | null;
  materialName?: string | null;
  variationType: 'ADDITIONAL_MATERIAL' | 'ADDITIONAL_WORK' | 'SCOPE_CHANGE';
  title: string;
  description: string;
  originalQuantity: string | number;
  additionalQuantity: string | number;
  revisedQuantity: string | number;
  unit: string;
  reason: string;
  measurements?: string | null;
  photos?: string[] | null;
  requesterName: string;
  requesterRole: string;
  status: VariationStatus;
  approvedBy?: string | null;
  approvedAt?: string | null;
  remarks?: string | null;
  createdAt: string;
}

export interface VerificationChecklistItem {
  id: number;
  workOrderId: number;
  ticketId: number;
  verifiedBy: string;
  scopeVerified: boolean;
  qualityVerified: boolean;
  materialsVerified: boolean;
  photosVerified: boolean;
  measurementsVerified: boolean;
  branchAcknowledgmentVerified: boolean;
  cleanlinessVerified: boolean;
  punchListItems?: string[] | null;
  punchListResolved: boolean;
  status: 'PASSED' | 'CORRECTIONS_NEEDED' | 'REJECTED';
  remarks?: string | null;
  verifiedAt: string;
}

// ============================================================================
// ======== PHASE E: PURCHASE, TAX ENGINE, PCC REGISTER & UBL PAYMENT =========
// ============================================================================

export type TaxType = 'SALES_GST' | 'INCOME_TAX_WHT' | 'PURCHASE_GST' | 'PURCHASE_WHT';

export interface TaxRuleItem {
  id: number;
  ruleCode: string;
  taxType: TaxType;
  jurisdiction: string;
  targetClassification: string;
  taxRate: string | number;
  calculationBasis: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  roundingMethod: string;
  isExemptionSupported: boolean;
  ruleReference?: string | null;
  status: 'ACTIVE' | 'SUPERSEDED' | 'SUSPENDED';
  description?: string | null;
  createdAt?: string;
}

export interface SupplierExemptionDocItem {
  id: number;
  vendorId: number;
  vendorName?: string;
  documentType: string;
  referenceNumber: string;
  issuingAuthority: string;
  issueDate: string;
  validFrom: string;
  validTo: string;
  documentUrl?: string | null;
  verificationStatus: 'PENDING' | 'VERIFIED' | 'REJECTED' | 'EXPIRED';
  verifiedBy?: string | null;
  verifiedAt?: string | null;
  remarks?: string | null;
  createdAt?: string;
}

export type PurchaseRequisitionStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'PO_CREATED' | 'REJECTED';

export interface PurchaseRequisitionItem {
  id: number;
  prNumber: string;
  projectId?: number | null;
  projectNumber?: string;
  projectName?: string;
  ticketId?: number | null;
  workOrderId?: number | null;
  requestedBy: string;
  department: string;
  requiredDate: string;
  items: Array<{
    itemCode?: string;
    description: string;
    quantity: number;
    unit: string;
    estimatedRate?: number;
    estimatedAmount?: number;
  }>;
  estimatedTotal: string | number;
  reason: string;
  urgencyLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'EMERGENCY';
  isDirectSiteDelivery: boolean;
  status: PurchaseRequisitionStatus;
  approvedBy?: string | null;
  approvedAt?: string | null;
  remarks?: string | null;
  createdAt?: string;
}

export interface SupplierBillItem {
  id: number;
  billNumber: string;
  supplierInvoiceNumber: string;
  vendorId: number;
  vendorName?: string;
  vendorCategory?: string;
  poId?: number | null;
  poNumber?: string;
  grnId?: number | null;
  projectId?: number | null;
  projectNumber?: string;
  costCenterType: 'PROJECT_SITE' | 'GENERAL_COMPANY';
  billDate: string;
  dueDate?: string | null;
  grossBeforeTax: string | number;
  gstRate: string | number;
  gstAmount: string | number;
  otherCharges: string | number;
  grossTotal: string | number;
  withholdingRate: string | number;
  withholdingAmount: string | number;
  netPayable: string | number;
  paidAmount: string | number;
  outstandingBalance: string | number;
  isGstInvoice: boolean;
  invoicePhotoUrl?: string | null;
  verificationStatus: 'PENDING' | 'VERIFIED' | 'REJECTED';
  verifiedBy?: string | null;
  verifiedAt?: string | null;
  paymentStatus: 'UNPAID' | 'PARTIALLY_PAID' | 'PAID';
  purchaseType: 'STANDARD_PO' | 'URGENT_SITE' | 'PETTY_CASH' | 'DIRECT_TO_SITE' | 'CONTRACTOR_SERVICE' | 'GENERAL_OFFICE';
  duplicateWarning: boolean;
  remarks?: string | null;
  createdAt?: string;
}

export type PccStatus = 'PCC_AWAITED' | 'RECEIVED' | 'VERIFIED' | 'REJECTED';

export interface PhysicalCompletionCertificateItem {
  id: number;
  pccNumber: string;
  externalPccNumber?: string | null;
  customerId: number;
  projectId: number;
  projectNumber?: string;
  projectName?: string;
  branchId: number;
  branchCode?: string;
  branchName?: string;
  ticketId?: number | null;
  ticketNumber?: string;
  workOrderId?: number | null;
  workOrderNumber?: string;
  certificateDate: string;
  receiptDate: string;
  receivedByEmployee: string;
  verificationStatus: 'PENDING' | 'VERIFIED' | 'REJECTED';
  verifiedBy?: string | null;
  verifiedAt?: string | null;
  rejectionReason?: string | null;
  documentUrl?: string | null;
  coveredScope?: string | null;
  linkedInvoiceIds?: number[] | null;
  status: PccStatus;
  remarks?: string | null;
  createdAt?: string;
}

export interface UblPaymentAdviceItem {
  id: number;
  adviceNumber: string;
  ublReferenceNo: string;
  adviceDate: string;
  relevantPeriod?: string | null;
  customerId: number;
  coveredInvoiceIds: number[];
  coveredInvoices?: Array<{
    id: number;
    invoiceNumber: string;
    subtotal: string | number;
    taxAmount: string | number;
    grandTotal: string | number;
    allocatedWht: string | number;
    allocatedNet: string | number;
  }>;
  totalInvoicesSubtotal: string | number;
  totalInvoicesGst: string | number;
  consolidatedBasisAmount: string | number;
  withholdingRate: string | number;
  consolidatedWithholdingAmount: string | number;
  otherDeductions: string | number;
  expectedNetPayment: string | number;
  actualAmountReceived: string | number;
  bankReference?: string | null;
  receiptDate?: string | null;
  reconciliationStatus: 'PENDING' | 'RECONCILED' | 'VARIANCE_FLAGGED';
  bankAccountId?: number | null;
  documentUrl?: string | null;
  remarks?: string | null;
  createdAt?: string;
}

export interface UblWithholdingCertItem {
  id: number;
  certificateNumber: string;
  issueDate: string;
  taxYear: string;
  coveredAdviceIds: number[];
  certifiedWithholdingAmount: string | number;
  erpRecordedWithholdingAmount: string | number;
  varianceAmount: string | number;
  documentUrl?: string | null;
  reconciliationStatus: 'MATCHED' | 'DISCREPANCY_FLAGGED';
  remarks?: string | null;
  createdAt?: string;
}

export interface TaxCalculationLogItem {
  id: number;
  transactionType: 'SALES_INVOICE' | 'SUPPLIER_BILL' | 'UBL_PAYMENT_ADVICE';
  transactionId: number;
  ruleCode: string;
  ruleVersion: number;
  taxType: string;
  taxableBasis: string | number;
  taxRate: string | number;
  calculatedTaxAmount: string | number;
  roundingMethod: string;
  calculatedAt: string;
}


